import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Palette,
  Upload,
  Save,
  RefreshCw,
  Globe,
  Mail,
  Phone,
  Building2,
  Loader2,
  Download,
  CheckCircle2,
  Eye,
  Copy,
  Settings,
  FileCode,
  Sparkles,
  Trash2
} from 'lucide-react';
import { PayslipHeaderPreview } from '../components/branding/PayslipHeaderPreview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BrandingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: 'MyHR Orbit',
    tagline: 'AI-Powered HRMS for Malaysian Businesses',
    logo_url: null,
    favicon_url: null,
    primary_color: '#10b981',
    secondary_color: '#14b8a6',
    accent_color: '#0f172a',
    footer_text: '© 2026 MyHR Orbit. Made with ❤️ in Malaysia',
    support_email: 'support@myhrorbit.com',
    sales_email: 'sales@myhrorbit.com',
    phone: '+60 12-345 6789',
    website: 'https://myhrorbit.com',
    show_powered_by: true
  });
  const [exportConfig, setExportConfig] = useState(null);
  
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.get(`${API}/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Error fetching branding settings:', err);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.put(`${API}/settings/branding`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branding settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.post(`${API}/settings/branding/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove the company logo? Payslips and PDFs will show the company name instead.')) return;
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.delete(`${API}/settings/branding/logo`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove logo');
    }
  };

  const handleExportConfig = async () => {
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.get(`${API}/admin/export-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportConfig(response.data);
      toast.success('Configuration exported!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export config');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    toast.success('Copied to clipboard!');
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Palette className="h-7 w-7 text-emerald-500" />
            White-Label Branding
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Customize your platform branding for easy client handover
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExportConfig}>
            <Download className="h-4 w-4 mr-2" />
            Export Config
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Brand Identity */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-500" />
            Brand Identity
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => updateSetting('company_name', e.target.value)}
                placeholder="Your Company Name"
              />
            </div>
            
            <div>
              <Label>Tagline</Label>
              <Input
                value={settings.tagline}
                onChange={(e) => updateSetting('tagline', e.target.value)}
                placeholder="Your company tagline"
              />
            </div>
            
            <div>
              <Label>Logo</Label>
              <div className="mt-2 flex items-center gap-4">
                {settings.logo_url ? (
                  <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} 
                      alt="Logo" 
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-slate-400" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="branding-upload-logo-btn"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {settings.logo_url ? 'Replace Logo' : 'Upload Logo'}
                  </Button>
                  {settings.logo_url && (
                    <Button variant="ghost" onClick={handleRemoveLogo} data-testid="branding-remove-logo-btn"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20">
                      <Trash2 className="h-4 w-4 mr-2" /> Remove Logo
                    </Button>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">Recommended: 200x200px, PNG or SVG. This logo is printed at the top of every payslip and PDF letter.</p>
            </div>

            <PayslipHeaderPreview
              companyName={settings.company_name}
              companyAddress={settings.company_address}
              logoUrl={settings.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : null}
            />
            
            <div>
              <Label>Footer Text</Label>
              <Input
                value={settings.footer_text}
                onChange={(e) => updateSetting('footer_text', e.target.value)}
                placeholder="Footer copyright text"
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="mb-0">Show "Powered by MyHR Orbit"</Label>
                <p className="text-xs text-slate-500">Display attribution in footer</p>
              </div>
              <Switch
                checked={settings.show_powered_by}
                onCheckedChange={(checked) => updateSetting('show_powered_by', checked)}
              />
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-500" />
            Color Scheme
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex gap-3 mt-2">
                <input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => updateSetting('primary_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={settings.primary_color}
                  onChange={(e) => updateSetting('primary_color', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Secondary Color</Label>
              <div className="flex gap-3 mt-2">
                <input
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => updateSetting('secondary_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={settings.secondary_color}
                  onChange={(e) => updateSetting('secondary_color', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Accent Color (Dark Background)</Label>
              <div className="flex gap-3 mt-2">
                <input
                  type="color"
                  value={settings.accent_color}
                  onChange={(e) => updateSetting('accent_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={settings.accent_color}
                  onChange={(e) => updateSetting('accent_color', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Color Preview */}
            <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: settings.accent_color }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: settings.primary_color }}>
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <span className="font-semibold text-white">{settings.company_name}</span>
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ backgroundColor: settings.primary_color }}>
                  Primary Button
                </button>
                <button className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: settings.secondary_color, color: settings.secondary_color }}>
                  Secondary
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-emerald-500" />
            Contact Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Support Email</Label>
              <Input
                type="email"
                value={settings.support_email}
                onChange={(e) => updateSetting('support_email', e.target.value)}
                placeholder="support@yourcompany.com"
              />
            </div>
            
            <div>
              <Label>Sales Email</Label>
              <Input
                type="email"
                value={settings.sales_email}
                onChange={(e) => updateSetting('sales_email', e.target.value)}
                placeholder="sales@yourcompany.com"
              />
            </div>
            
            <div>
              <Label>Phone Number</Label>
              <Input
                value={settings.phone}
                onChange={(e) => updateSetting('phone', e.target.value)}
                placeholder="+60 12-345 6789"
              />
            </div>
            
            <div>
              <Label>Website URL</Label>
              <Input
                value={settings.website}
                onChange={(e) => updateSetting('website', e.target.value)}
                placeholder="https://yourcompany.com"
              />
            </div>
          </div>
        </div>

        {/* Export Configuration */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <FileCode className="h-5 w-5 text-emerald-500" />
            Export for Handover
          </h2>
          
          <p className="text-slate-500 dark:text-slate-400 mb-4 text-sm">
            Export your complete configuration for white-label deployment. This includes branding settings, setup instructions, and required environment variables.
          </p>
          
          {exportConfig ? (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 max-h-64 overflow-auto">
                <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                  {JSON.stringify(exportConfig, null, 2)}
                </pre>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => copyToClipboard(exportConfig)} className="flex-1">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(exportConfig, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'white-label-config.json';
                    a.click();
                  }}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download JSON
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={handleExportConfig} className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Generate Export Configuration
            </Button>
          )}
          
          <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-2">Setup Instructions</h4>
            <ol className="text-sm text-emerald-700 dark:text-emerald-300 space-y-1 list-decimal list-inside">
              <li>Update environment variables with client's domain</li>
              <li>Configure MongoDB connection string</li>
              <li>Set up email provider (Resend recommended)</li>
              <li>Update branding in admin panel</li>
              <li>Test all modules before going live</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
